import * as React from "react";
import { Link } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { useLocale } from "@/contexts/LocaleContext";
import { readConsent, writeConsent, COOKIE_SETTINGS_EVENT } from "@/lib/cookie-consent";
import { startOpenAiConsentBridge } from "@/lib/openaiConsentBridge";
import { mirrorShopifyConsentChoice } from "@/lib/shopifyConsentMirror";

/**
 * شريط موافقة الكوكيز — خياران فقط: أوافق / لا أوافق.
 * الأساسيّة دائماً مفعّلة؛ الخياران يتحكّمان بالتحليلات والتسويق.
 */
export function CookieConsentBanner() {
  const { t } = useLocale();
  // ⚡ LCP: الشريط يُرسم في HTML الأوّل (SSR) فيصبح جزءاً من أوّل رسم بدل أن يظهر
  // بعد hydration ويصير عنصر LCP متأخّراً. مَن سبق أن وافق يُخفى قبل الرسم عبر
  // سكربت `lecmo-has-consent` + قاعدة CSS في styles.css (لا وميض).
  const [dismissed, setDismissed] = React.useState(false);
  const [reopened, setReopened] = React.useState(false);
  const panel = React.useRef<HTMLDivElement>(null);
  const trigger = React.useRef<HTMLElement | null>(null);
  React.useEffect(() => {
    const reopen = () => {
      trigger.current =
        document.activeElement instanceof HTMLElement ? document.activeElement : null;
      setReopened(true);
      setDismissed(false);
    };
    window.addEventListener(COOKIE_SETTINGS_EVENT, reopen);
    const stop = startOpenAiConsentBridge();
    return () => {
      window.removeEventListener(COOKIE_SETTINGS_EVENT, reopen);
      stop();
    };
  }, []);
  React.useEffect(() => {
    if (reopened && !dismissed) panel.current?.focus();
  }, [reopened, dismissed]);

  React.useEffect(() => {
    // مصدر حقيقة واحد: readConsent() يقرأ الكوكي ثمّ localStorage كنسخة احتياطيّة.
    if (readConsent()) setDismissed(true);
  }, []);

  if (dismissed) return null;

  const save = (analytics: boolean, ads: boolean) => {
    writeConsent({ analytics, ads });
    mirrorShopifyConsentChoice({ analytics, ads });
    setDismissed(true);
    setReopened(false);
    trigger.current?.focus();
  };

  return (
    <div
      ref={panel}
      id="lecmo-cookie-choices"
      role="region"
      aria-label={t("footer.cookies")}
      tabIndex={-1}
      data-reopened={reopened ? "true" : undefined}
      className="cookie-consent-banner fixed inset-x-0 bottom-0 z-[70] px-3 pb-3 md:px-6 md:pb-5"
      style={{ marginBottom: "var(--mobile-nav-height, 0rem)" }}
    >
      <div className="mx-auto max-w-4xl border border-border bg-card/97 p-4 shadow-2xl backdrop-blur md:flex md:items-end md:justify-between md:gap-6 md:p-5">
        <div className="md:flex-1">
          <p className="text-[13px] font-medium leading-relaxed text-foreground md:text-[14px]">
            {t("cookie_consent.body")}
          </p>
          <p className="mt-1.5 text-[11px] text-muted-foreground">
            <Link to="/cookies" className="underline underline-offset-4 hover:text-foreground">
              {t("cookie_consent.policy_link")}
            </Link>
          </p>
        </div>

        <div className="mt-4 flex items-center gap-2 md:mt-0">
          <Button
            size="sm"
            className="min-h-[40px] flex-1 text-[11px] uppercase tracking-[0.16em] md:flex-none"
            onClick={() => save(true, true)}
          >
            {t("cookie_consent.accept")}
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="min-h-[40px] flex-1 text-[11px] uppercase tracking-[0.16em] md:flex-none"
            onClick={() => save(false, false)}
          >
            {t("cookie_consent.reject")}
          </Button>
        </div>
      </div>
    </div>
  );
}
